import { useState, useEffect, useCallback } from 'react';
import { formatDateTime } from '../../utils/dateFormatting';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Database, Play, Eye, RotateCcw, Trash2, CheckCircle2, XCircle, Loader2, AlertTriangle, Save, FlaskConical,
} from 'lucide-react';
import { toast } from 'sonner';

const AllocateMigration = () => {
  const { api } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};
  const textMain = isDark ? 'text-white' : 'text-slate-900';
  const labelMuted = isDark ? 'text-slate-400' : 'text-slate-600';

  const [tenants, setTenants] = useState([]);
  const [clientId, setClientId] = useState('');
  const [loadingTenants, setLoadingTenants] = useState(true);

  // Connection form
  const [form, setForm] = useState({ server: '', database: '', user: '', password: '', port: 1433 });
  const [storedConn, setStoredConn] = useState(null);
  const [savingConn, setSavingConn] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Pipeline + runs
  const [entities, setEntities] = useState([]);
  const [selectedEntities, setSelectedEntities] = useState(new Set()); // empty = all
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [activeRun, setActiveRun] = useState(null);
  const [errorsView, setErrorsView] = useState(null); // { runId, errors }

  // Confirm modal
  const [confirmAction, setConfirmAction] = useState(null); // 'delete-conn' | 'rollback'
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);

  // ---------- DATA ----------
  const fetchTenants = useCallback(async () => {
    try {
      const res = await api.get('/api/super-admin/tenants-list');
      setTenants(res.data || []);
    } catch (err) {
      toast.error('Failed to load tenants', { description: err.response?.data?.detail || err.message });
    } finally {
      setLoadingTenants(false);
    }
  }, [api]);

  const fetchEntities = useCallback(async () => {
    try {
      const res = await api.get('/api/super-admin/allocate-migration/entities');
      setEntities(res.data?.entities || []);
    } catch (err) {
      // non-blocking
    }
  }, [api]);

  const fetchConnection = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const res = await api.get(`/api/super-admin/allocate-migration/connections/${cid}`);
      setStoredConn(res.data);
      setForm({
        server: res.data.server || '',
        database: res.data.database || '',
        user: res.data.user || '',
        password: '',
        port: res.data.port || 1433,
      });
    } catch (err) {
      if (err.response?.status === 404) {
        setStoredConn(null);
        setForm({ server: '', database: '', user: '', password: '', port: 1433 });
      } else {
        toast.error('Failed to load connection', { description: err.response?.data?.detail || err.message });
      }
    }
  }, [api]);

  const fetchRuns = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const res = await api.get(`/api/super-admin/allocate-migration/tenants/${cid}/runs`);
      setRuns(res.data?.runs || []);
    } catch (err) {
      // non-blocking
    }
  }, [api]);

  useEffect(() => { fetchTenants(); fetchEntities(); }, [fetchTenants, fetchEntities]);
  useEffect(() => {
    setTestResult(null);
    setActiveRun(null);
    fetchConnection(clientId);
    fetchRuns(clientId);
  }, [clientId, fetchConnection, fetchRuns]);

  // ---------- ACTIONS ----------
  const saveConnection = async () => {
    if (!clientId) return toast.error('Select a tenant first');
    if (!form.server || !form.database || !form.user || !form.password) {
      return toast.error('Server, database, user and password are required to save');
    }
    setSavingConn(true);
    try {
      await api.put(`/api/super-admin/allocate-migration/connections/${clientId}`, form);
      toast.success('Connection saved');
      await fetchConnection(clientId);
    } catch (err) {
      toast.error('Save failed', { description: err.response?.data?.detail || err.message });
    } finally {
      setSavingConn(false);
    }
  };

  const deleteConnection = async () => {
    setConfirmAction(null);
    try {
      await api.delete(`/api/super-admin/allocate-migration/connections/${clientId}`);
      toast.success('Connection deleted');
      setStoredConn(null);
      setForm({ server: '', database: '', user: '', password: '', port: 1433 });
    } catch (err) {
      toast.error('Delete failed', { description: err.response?.data?.detail || err.message });
    }
  };

  const testConnection = async () => {
    if (!clientId) return toast.error('Select a tenant first');
    setTesting(true);
    setTestResult(null);
    try {
      const body = form.password
        ? { ...form }
        : { client_id: clientId };
      const res = await api.post('/api/super-admin/allocate-migration/test-connection', body);
      setTestResult(res.data);
      if (res.data?.ok) toast.success('Connection OK');
      else toast.error('Connection failed', { description: res.data?.error });
    } catch (err) {
      toast.error('Test failed', { description: err.response?.data?.detail || err.message });
    } finally {
      setTesting(false);
    }
  };

  const runMigration = async (dry) => {
    if (!clientId) return toast.error('Select a tenant first');
    const entityList = selectedEntities.size > 0 ? Array.from(selectedEntities) : null;
    setRunning(true);
    setActiveRun(null);
    try {
      const url = `/api/super-admin/allocate-migration/tenants/${clientId}/${dry ? 'preview' : 'run'}`;
      const body = form.password
        ? { ...form, entities: entityList, update_existing: updateExisting }
        : { entities: entityList, update_existing: updateExisting };
      const res = await api.post(url, body);
      const queuedRun = res.data;
      setActiveRun(queuedRun);
      toast.info(dry ? 'Preview started…' : 'Migration started…', {
        description: `Run ${queuedRun.id?.slice(0, 8)}. Polling for progress.`,
      });

      // Poll the run until it reaches a terminal status
      const terminal = new Set(['completed', 'failed', 'rolled_back']);
      const runId = queuedRun.id;
      let lastStatus = queuedRun.status;
      const started = Date.now();
      // Safety cap: 10 minutes
      while (Date.now() - started < 10 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          const poll = await api.get(`/api/super-admin/allocate-migration/runs/${runId}`);
          setActiveRun(poll.data);
          lastStatus = poll.data?.status;
          if (terminal.has(lastStatus)) break;
        } catch (e) {
          // ignore transient polling errors
        }
      }
      if (terminal.has(lastStatus)) {
        if (lastStatus === 'completed') {
          toast.success(dry ? 'Preview complete' : 'Migration complete');
        } else if (lastStatus === 'failed') {
          toast.error('Run failed — see Errors for details');
        }
      } else {
        toast.warning('Run still in progress after 10 minutes — keep this page open or refresh later.');
      }
      await fetchRuns(clientId);
    } catch (err) {
      toast.error('Run failed', { description: err.response?.data?.detail || err.message });
    } finally {
      setRunning(false);
    }
  };

  const openRun = async (run) => {
    try {
      const res = await api.get(`/api/super-admin/allocate-migration/runs/${run.id}`);
      setActiveRun(res.data);
    } catch (err) {
      toast.error('Failed to load run', { description: err.response?.data?.detail || err.message });
    }
  };

  const openErrors = async (runId) => {
    try {
      const res = await api.get(`/api/super-admin/allocate-migration/runs/${runId}/errors`);
      setErrorsView({ runId, errors: res.data?.errors || [] });
    } catch (err) {
      toast.error('Failed to load errors', { description: err.response?.data?.detail || err.message });
    }
  };

  const rollback = async () => {
    if (!rollbackTarget) return;
    const runId = rollbackTarget.id;
    setRollbackTarget(null);
    try {
      await api.post(`/api/super-admin/allocate-migration/runs/${runId}/rollback`);
      toast.success('Run rolled back');
      await fetchRuns(clientId);
      if (activeRun?.id === runId) {
        const res = await api.get(`/api/super-admin/allocate-migration/runs/${runId}`);
        setActiveRun(res.data);
      }
    } catch (err) {
      toast.error('Rollback failed', { description: err.response?.data?.detail || err.message });
    }
  };

  // ---------- RENDER ----------
  const runStatusBadge = (run) => {
    const map = {
      completed:    { cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500', icon: CheckCircle2, label: 'Completed' },
      failed:       { cls: 'bg-red-500/20 text-red-300 border-red-500',            icon: XCircle,      label: 'Failed' },
      running:      { cls: 'bg-blue-500/20 text-blue-300 border-blue-500',         icon: Loader2,      label: 'Running' },
      queued:       { cls: 'bg-slate-500/20 text-slate-300 border-slate-500',      icon: Loader2,      label: 'Queued' },
      rolled_back:  { cls: 'bg-amber-500/20 text-amber-300 border-amber-500',      icon: RotateCcw,    label: 'Rolled back' },
    };
    const m = map[run.status] || map.queued;
    const Icon = m.icon;
    return (
      <Badge variant="outline" className={`gap-1 ${m.cls}`}>
        <Icon className={`h-3 w-3 ${run.status === 'running' || run.status === 'queued' ? 'animate-spin' : ''}`} />
        {m.label}{run.dry_run ? ' · DRY' : ''}
      </Badge>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className={`text-2xl font-bold ${textMain} flex items-center gap-2`}>
          <Database className="h-6 w-6 text-[#ffd108]" /> Allocate Migration
        </h1>
        <p className={`text-sm ${labelMuted} mt-1`}>
          Import data from a client's Allocate (VB.NET / SQL Server) database into Core.
        </p>
      </div>

      {/* Tenant + connection */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
        <CardHeader>
          <CardTitle className={textMain}>1. Select Tenant & Connection</CardTitle>
          <CardDescription className={labelMuted}>
            Pick the destination Core tenant. Connection details are encrypted at rest and tied to the tenant.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className={textMain}>Destination tenant</Label>
            <Select value={clientId} onValueChange={setClientId} disabled={loadingTenants}>
              <SelectTrigger className="mt-1" data-testid="alloc-tenant-select">
                <SelectValue placeholder={loadingTenants ? 'Loading…' : 'Select a tenant'} />
              </SelectTrigger>
              <SelectContent>
                {tenants.map((t) => (
                  <SelectItem key={t.client_id} value={t.client_id}>
                    {t.company_name} ({t.client_id})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {clientId && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className={textMain}>SQL Server</Label>
                  <Input placeholder="server.example.com or 10.0.0.5"
                    value={form.server}
                    onChange={(e) => setForm({ ...form, server: e.target.value })}
                    data-testid="alloc-conn-server"
                  />
                </div>
                <div>
                  <Label className={textMain}>Database</Label>
                  <Input placeholder="AllocateProdDB"
                    value={form.database}
                    onChange={(e) => setForm({ ...form, database: e.target.value })}
                    data-testid="alloc-conn-database"
                  />
                </div>
                <div>
                  <Label className={textMain}>SQL User</Label>
                  <Input placeholder="sa or read-only user"
                    value={form.user}
                    onChange={(e) => setForm({ ...form, user: e.target.value })}
                    data-testid="alloc-conn-user"
                  />
                </div>
                <div>
                  <Label className={textMain}>Password {storedConn?.has_password && '(leave blank to use stored)'}</Label>
                  <Input type="password" placeholder={storedConn?.has_password ? '••••••••' : 'Password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="alloc-conn-password"
                  />
                </div>
                <div>
                  <Label className={textMain}>Port</Label>
                  <Input type="number"
                    value={form.port}
                    onChange={(e) => setForm({ ...form, port: parseInt(e.target.value, 10) || 1433 })}
                    data-testid="alloc-conn-port"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={testConnection} disabled={testing} variant="outline" data-testid="alloc-test-conn-btn">
                  {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FlaskConical className="mr-2 h-4 w-4" />}
                  Test connection
                </Button>
                <Button onClick={saveConnection} disabled={savingConn} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]" data-testid="alloc-save-conn-btn">
                  {savingConn ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  {storedConn ? 'Update saved connection' : 'Save connection'}
                </Button>
                {storedConn && (
                  <Button variant="outline" onClick={() => setConfirmAction('delete-conn')}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    data-testid="alloc-delete-conn-btn"
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete saved
                  </Button>
                )}
                {testResult && (
                  testResult.ok ? (
                    <Badge variant="outline" className="bg-emerald-500/20 text-emerald-300 border-emerald-500 gap-1">
                      <CheckCircle2 className="h-3 w-3" /> {testResult.database}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-red-500/20 text-red-300 border-red-500 gap-1">
                      <XCircle className="h-3 w-3" /> {String(testResult.error || 'Failed').slice(0, 80)}
                    </Badge>
                  )
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pipeline overview */}
      {clientId && (
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
          <CardHeader>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <CardTitle className={textMain}>2. Pipeline</CardTitle>
                <CardDescription className={labelMuted}>
                  Pick which entities to migrate. Order is fixed (dependencies first). Leave none ticked to run the full pipeline.
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setSelectedEntities(new Set(entities.map(e => e.entity_type)))} data-testid="alloc-select-all-btn">
                  Select all
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedEntities(new Set())} data-testid="alloc-clear-selection-btn">
                  Clear selection
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-4">
              {entities.map((e) => {
                const checked = selectedEntities.has(e.entity_type);
                return (
                  <label
                    key={e.entity_type}
                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                      checked
                        ? (isDark ? 'border-[#ffd108] bg-[#ffd108]/10' : 'border-[#ffd108] bg-[#ffd108]/10')
                        : (isDark ? 'border-slate-700 hover:bg-slate-700/40' : 'border-slate-200 hover:bg-slate-100')
                    }`}
                    data-testid={`alloc-entity-row-${e.entity_type}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(ev) => {
                        const next = new Set(selectedEntities);
                        if (ev.target.checked) next.add(e.entity_type);
                        else next.delete(e.entity_type);
                        setSelectedEntities(next);
                      }}
                      className="h-4 w-4 accent-[#ffd108]"
                      data-testid={`alloc-entity-check-${e.entity_type}`}
                    />
                    <span className={`text-[10px] ${labelMuted}`}>#{e.order}</span>
                    <span className={`text-sm ${textMain} flex-1`}>{e.entity_type}</span>
                    <span className={`text-[10px] ${labelMuted} font-mono opacity-70`}>{e.sp}</span>
                  </label>
                );
              })}
            </div>
            <div className={`text-xs ${labelMuted} mb-3`}>
              {selectedEntities.size === 0
                ? 'Will run: all entities in pipeline order.'
                : `Will run: ${selectedEntities.size} selected entit${selectedEntities.size === 1 ? 'y' : 'ies'} (in pipeline order; unselected entities are skipped).`}
            </div>
            <div className="flex flex-wrap items-center gap-3 mb-1">
              <Button onClick={() => runMigration(true)} disabled={running} variant="outline" data-testid="alloc-preview-btn">
                {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
                Preview (dry-run)
              </Button>
              <Button onClick={() => runMigration(false)} disabled={running} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]" data-testid="alloc-run-btn">
                {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                Run migration
              </Button>
              <label className={`flex items-center gap-1.5 text-xs cursor-pointer select-none ${labelMuted}`}>
                <input
                  type="checkbox"
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                  data-testid="update-existing-checkbox"
                  className="accent-[#ffd108]"
                />
                Update existing records
              </label>
            </div>
            {updateExisting && (
              <p className={`text-xs ${isDark ? 'text-amber-400/80' : 'text-amber-700'} mt-1`}>
                Re-applies Allocate values to records already migrated. Overwrites edits made in Core to migrated fields.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Active run summary */}
      {activeRun && (
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
          <CardHeader>
            <CardTitle className={`${textMain} flex items-center gap-2`}>
              {runStatusBadge(activeRun)}
              Run · {activeRun.id?.slice(0, 8)}
            </CardTitle>
            <CardDescription className={labelMuted}>
              {activeRun.dry_run ? 'Dry run — no data committed.' : 'Real migration — rollback available below.'}
              {activeRun.update_existing && ' Update-existing mode ON.'}
              {activeRun.completed_at && ` Completed ${formatDateTime(activeRun.completed_at)}.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className={`min-w-full text-sm ${textMain}`}>
                <thead className={`${labelMuted} text-xs uppercase`}>
                  <tr>
                    <th className="text-left py-1 pr-4">Entity</th>
                    <th className="text-right py-1 pr-4">Total</th>
                    <th className="text-right py-1 pr-4">Inserted</th>
                    <th className="text-right py-1 pr-4">Updated</th>
                    <th className="text-right py-1 pr-4">Matched</th>
                    <th className="text-right py-1 pr-4">Skipped</th>
                    <th className="text-right py-1 pr-4">Errors</th>
                    <th className="text-left py-1 pr-4">SP</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(activeRun.entities || {}).map(([etype, stats]) => (
                    <tr key={etype} className={`${isDark ? 'border-t border-slate-700' : 'border-t border-slate-200'}`}>
                      <td className="py-1 pr-4">{etype}</td>
                      <td className="py-1 pr-4 text-right">{stats.total ?? '—'}</td>
                      <td className="py-1 pr-4 text-right text-emerald-400">{stats.inserted ?? '—'}</td>
                      <td className="py-1 pr-4 text-right text-cyan-400">{stats.updated ?? '—'}</td>
                      <td className="py-1 pr-4 text-right text-blue-400">{stats.matched ?? '—'}</td>
                      <td className="py-1 pr-4 text-right text-amber-300">{stats.skipped ?? '—'}</td>
                      <td className="py-1 pr-4 text-right text-red-400">{stats.errors ?? '—'}</td>
                      <td className="py-1 pr-4 text-xs opacity-70 font-mono">{stats.sp}</td>
                    </tr>
                  ))}
                  {Object.keys(activeRun.entities || {}).length === 0 && (
                    <tr><td colSpan={8} className={`py-3 ${labelMuted}`}>No entity results.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => openErrors(activeRun.id)} data-testid="alloc-view-errors-btn">
                View errors
              </Button>
              {!activeRun.dry_run && activeRun.status === 'completed' && (
                <Button variant="outline" onClick={() => setRollbackTarget(activeRun)}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  data-testid="alloc-rollback-btn"
                >
                  <RotateCcw className="mr-2 h-4 w-4" /> Rollback this run
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Run history */}
      {clientId && (
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
          <CardHeader>
            <CardTitle className={textMain}>3. Run history</CardTitle>
          </CardHeader>
          <CardContent>
            {runs.length === 0 ? (
              <div className={`text-sm ${labelMuted}`}>No runs yet for this tenant.</div>
            ) : (
              <div className="space-y-2">
                {runs.map((r) => (
                  <button key={r.id} type="button" onClick={() => openRun(r)}
                    className={`w-full text-left p-3 rounded border ${isDark ? 'border-slate-700 hover:bg-slate-700/40' : 'border-slate-200 hover:bg-slate-100'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {runStatusBadge(r)}
                        <span className={`text-xs ${labelMuted}`}>{r.id?.slice(0, 8)}</span>
                      </div>
                      <span className={`text-xs ${labelMuted}`}>
                        {r.completed_at ? formatDateTime(r.completed_at) : r.created_at ? formatDateTime(r.created_at) : ''}
                      </span>
                    </div>
                    <div className={`text-xs mt-1 ${labelMuted}`}>
                      by {r.created_by || 'unknown'} · {r.source?.database || ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete connection confirm */}
      <Dialog open={confirmAction === 'delete-conn'} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
          <DialogHeader>
            <DialogTitle className={textMain}>Delete saved connection?</DialogTitle>
            <DialogDescription className={labelMuted}>
              The encrypted credentials for this tenant will be removed. Future runs will require re-entering the password.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button onClick={deleteConnection} className="bg-red-500 text-white hover:bg-red-600">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback confirm */}
      <Dialog open={!!rollbackTarget} onOpenChange={(o) => !o && setRollbackTarget(null)}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
          <DialogHeader>
            <DialogTitle className={textMain}>Rollback run?</DialogTitle>
            <DialogDescription className={labelMuted}>
              All Customers, Sites and Customer Contacts inserted by this run will be deleted from the Core database for this tenant.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300 flex gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>This will not affect the source Allocate database. It only undoes inserts tagged with this run's ID.</span>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRollbackTarget(null)}>Cancel</Button>
            <Button onClick={rollback} className="bg-red-500 text-white hover:bg-red-600">Rollback</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Errors viewer */}
      <Dialog open={!!errorsView} onOpenChange={(o) => !o && setErrorsView(null)}>
        <DialogContent className={`${isDark ? 'bg-slate-800 border-slate-700' : ''} max-w-3xl`}>
          <DialogHeader>
            <DialogTitle className={textMain}>Errors · run {errorsView?.runId?.slice(0, 8)}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-96">
            {(errorsView?.errors || []).length === 0 ? (
              <div className={`text-sm ${labelMuted}`}>No errors recorded.</div>
            ) : (
              <table className={`min-w-full text-xs ${textMain}`}>
                <thead className={`${labelMuted} uppercase`}>
                  <tr>
                    <th className="text-left py-1 pr-4">Entity</th>
                    <th className="text-left py-1 pr-4">LegacyId</th>
                    <th className="text-left py-1 pr-4">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {errorsView.errors.map((e) => (
                    <tr key={e.id} className={`${isDark ? 'border-t border-slate-700' : 'border-t border-slate-200'}`}>
                      <td className="py-1 pr-4">{e.entity_type}</td>
                      <td className="py-1 pr-4 font-mono">{e.legacy_id}</td>
                      <td className="py-1 pr-4">{e.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllocateMigration;
